import { Box, Fade } from '@mui/material';
import { useEffect, useState } from 'react';

import { EpisodeCard as EpisodeCardType, Referrer } from '@/types';

import {
  EpisodeCard,
  EpisodeCardMediumDimensions,
  EpisodeCardSkeleton,
} from '../EpisodeCard';
import { EPISODE_GRID_CHUNK_SIZE, SCROLL_THRESHOLD } from './constants';

interface EpisodeGridProps {
  episodes?: EpisodeCardType[];
  isLoading?: boolean;
  referrer?: Referrer;
}

export const EpisodeGrid = ({
  episodes = [],
  isLoading = false,
  referrer = {},
}: EpisodeGridProps) => {
  const [visibleEpisodeCount, setVisibleEpisodeCount] = useState(
    EPISODE_GRID_CHUNK_SIZE,
  );

  useEffect(() => {
    const loadMore = () => {
      if (visibleEpisodeCount >= episodes.length) return;

      const currentScrollPosition =
        window.innerHeight + document.documentElement.scrollTop;
      const scrollThreshold =
        (document.scrollingElement?.scrollHeight || 0) - SCROLL_THRESHOLD;

      if (currentScrollPosition >= scrollThreshold) {
        setVisibleEpisodeCount(
          Math.min(
            visibleEpisodeCount + EPISODE_GRID_CHUNK_SIZE,
            episodes.length,
          ),
        );
      }
    };

    window.addEventListener('scroll', loadMore);
    return () => window.removeEventListener('scroll', loadMore);
  }, [visibleEpisodeCount, episodes.length]);

  const visibleEpisodes = episodes.slice(0, visibleEpisodeCount);

  return (
    <Box sx={episodeGridStyles.root}>
      {!isLoading
        ? visibleEpisodes?.map((episode, index) => (
            <Fade
              key={episode.episodeId}
              in
              timeout={
                Math.log10((index % EPISODE_GRID_CHUNK_SIZE) + 2) * 1000
              }>
              <Box key={episode.episodeId} sx={{ minWidth: 0 }}>
                <EpisodeCard
                  stretch
                  episode={episode}
                  referrer={{ ...referrer, position: index + 1 }}
                />
              </Box>
            </Fade>
          ))
        : Array.from(Array(8)).map((_, index) => (
            <Box key={index} sx={{ minWidth: 0 }}>
              <EpisodeCardSkeleton includeText={true} stretch />
            </Box>
          ))}
    </Box>
  );
};

export const episodeGridStyles = {
  root: {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'repeat(2, 1fr)',
      sm: `repeat(auto-fill, minmax(${Math.round(EpisodeCardMediumDimensions.width * 0.85)}px, 1fr))`,
    },
    mt: 2,
    gap: {
      xs: 1.5,
      sm: 3,
    },
  },
};
